import { useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { pushModalLayer } from '../../lib/modalLayer';
type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  anchorRef?: RefObject<HTMLElement | null>;
};

export function TicketBubblePanel({
  open,
  title,
  onClose,
  children,
  className,
  anchorRef,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const popLayer = pushModalLayer();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      popLayer();
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    if (!panel) return;

    if (window.matchMedia('(max-width: 1100px)').matches) {
      panel.style.removeProperty('top');
      panel.style.removeProperty('right');
      panel.style.removeProperty('left');
      panel.style.removeProperty('bottom');
      return;
    }
    const anchor = anchorRef?.current;
    if (!anchor) return;

    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const gap = 10;
      if (rect.width < 1 || rect.height < 1) {
        panel.style.top = '72px';
        panel.style.right = '12px';
        panel.style.left = '12px';
        panel.style.setProperty('--bubble-tail-right', '28px');
        return;
      }
      const top = rect.bottom + gap;
      const right = Math.max(12, window.innerWidth - rect.right);
      panel.style.top = `${top}px`;
      panel.style.right = `${right}px`;
      panel.style.left = 'auto';
      panel.style.setProperty('--bubble-tail-right', `${Math.min(rect.width / 2, 28)}px`);
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef]);

  if (!open) return null;

  return createPortal(
    <>
      <button
        type="button"
        className="ticket-bubble-panel__backdrop"
        aria-label="Close panel"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={['ticket-bubble-panel', className].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="ticket-bubble-panel__head">
          <h3 className="ticket-bubble-panel__title">{title}</h3>
          <button type="button" className="ticket-bubble-panel__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="ticket-bubble-panel__body">{children}</div>
      </div>
    </>,
    document.body,
  );
}
